import type { LucideIcon } from "lucide-react";
import {
  Bike,
  Boxes,
  CircleHelp,
  ClipboardList,
  FileText,
  FolderTree,
  Image,
  Images,
  LayoutDashboard,
  LayoutTemplate,
  LifeBuoy,
  MapPinned,
  Megaphone,
  MessageSquareWarning,
  Package,
  PackageCheck,
  Radio,
  Repeat,
  Settings,
  Sparkles,
  Sprout,
  Star,
  TicketPercent,
  Truck,
  Undo2,
  Users,
  Wallet,
} from "lucide-react";

import type { PermissionSubject } from "@milqit/shared-types";

import type { NavCounts } from "./api/insights";
import type { AppAbility } from "./auth/ability";

export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  /** The area this page shows; hidden from roles that can't read it. */
  subject: PermissionSubject;
  /** Key of the live count shown as a sidebar badge. */
  badge?: keyof NavCounts;
  keywords?: string[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    items: [
      {
        title: "Dashboard",
        url: "/",
        subject: "Dashboard",
        icon: LayoutDashboard,
        keywords: ["home", "revenue", "kpi"],
      },
      {
        title: "Live orders",
        url: "/live",
        subject: "Order",
        icon: Radio,
        badge: "liveOrders",
        keywords: ["board", "queue", "kanban", "realtime"],
      },
    ],
  },
  {
    label: "Sales",
    items: [
      {
        title: "Orders",
        url: "/orders",
        subject: "Order",
        icon: ClipboardList,
        badge: "newOrders",
        keywords: ["crate", "status"],
      },
      {
        title: "Subscriptions",
        url: "/subscriptions",
        subject: "Subscription",
        icon: Repeat,
        keywords: ["milk plan", "recurring", "pause", "skip"],
      },
      {
        title: "Customers",
        url: "/customers",
        subject: "Customer",
        icon: Users,
        keywords: ["households", "people"],
      },
      {
        title: "Support",
        url: "/support",
        subject: "Ticket",
        icon: LifeBuoy,
        badge: "openTickets",
        keywords: ["tickets", "complaints", "help"],
      },
      {
        title: "Help FAQs",
        url: "/help-faqs",
        subject: "HelpFaq",
        icon: CircleHelp,
        keywords: ["faq", "questions", "answers", "help centre"],
      },
    ],
  },
  {
    label: "Catalogue",
    items: [
      {
        title: "Products",
        url: "/products",
        subject: "Product",
        icon: Package,
        keywords: ["sku", "milk", "dairy"],
      },
      {
        title: "Categories",
        url: "/categories",
        subject: "Category",
        icon: FolderTree,
        keywords: ["aisles"],
      },
      {
        title: "Farmers",
        url: "/farmers",
        subject: "Farmer",
        icon: Sprout,
        keywords: ["kisan", "farms", "dairy partners", "suppliers"],
      },
      {
        title: "Reviews",
        url: "/reviews",
        subject: "Product",
        icon: Star,
        keywords: ["ratings", "moderation", "hide", "customer feedback"],
      },
      {
        title: "Media library",
        url: "/media",
        subject: "Media",
        icon: Images,
        keywords: ["images", "photos", "uploads", "pictures", "gallery"],
      },
      {
        title: "Inventory",
        url: "/inventory",
        subject: "Inventory",
        icon: Boxes,
        badge: "lowStock",
        keywords: ["stock", "restock"],
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        title: "Packing",
        url: "/packing",
        subject: "Delivery",
        icon: PackageCheck,
        keywords: ["pick list", "crates", "labels", "bottles"],
      },
      {
        title: "Deliveries",
        url: "/deliveries",
        subject: "Delivery",
        icon: Truck,
        badge: "unassigned",
        keywords: ["routes", "partners"],
      },
      {
        title: "Riders",
        url: "/riders",
        subject: "Delivery",
        icon: Bike,
        keywords: ["shifts", "cash", "cod", "check-in"],
      },
      {
        title: "Rider map",
        url: "/rider-map",
        subject: "Delivery",
        icon: MapPinned,
        keywords: ["live", "location", "map", "gps", "tracking"],
      },
    ],
  },
  {
    label: "Marketing",
    items: [
      {
        title: "Coupons",
        url: "/coupons",
        subject: "Coupon",
        icon: TicketPercent,
        keywords: ["offers", "discount", "promo"],
      },
      {
        title: "Banners",
        url: "/banners",
        subject: "Banner",
        icon: Image,
        keywords: ["cms", "hero"],
      },
      {
        title: "Home page",
        url: "/home-page",
        subject: "Banner",
        icon: LayoutTemplate,
        keywords: ["cms", "home page", "sections", "copy", "flowers", "promises"],
      },
      {
        title: "Recommendations",
        url: "/recommendations",
        subject: "Recommendation",
        icon: Sparkles,
        keywords: ["bought together", "buy again", "popular", "suggestions", "rails"],
      },
      {
        title: "Announcements",
        url: "/announcements",
        subject: "Announcement",
        icon: MessageSquareWarning,
        keywords: ["alert", "popup", "modal", "notice", "outage", "warning", "banner strip"],
      },
      {
        title: "Notifications",
        url: "/notifications",
        subject: "Campaign",
        icon: Megaphone,
        keywords: ["campaigns", "whatsapp", "sms", "push", "templates"],
      },
    ],
  },
  {
    label: "Finance",
    items: [
      {
        title: "Refunds",
        url: "/refunds",
        subject: "Refund",
        icon: Undo2,
        badge: "pendingRefunds",
        keywords: ["returns", "money back"],
      },
      {
        title: "Wallet",
        url: "/wallet",
        subject: "Wallet",
        icon: Wallet,
        keywords: ["ledger", "credit", "balance"],
      },
      {
        title: "Invoices",
        url: "/invoices",
        subject: "Invoice",
        icon: FileText,
        keywords: ["gst", "tax", "bill"],
      },
    ],
  },
  {
    label: "System",
    items: [
      {
        title: "Settings",
        url: "/settings",
        subject: "Settings",
        icon: Settings,
        keywords: ["hubs", "pincodes", "slots", "cut-off", "fees", "gst"],
      },
    ],
  },
];

export const NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

export function isNavActive(pathname: string, url: string): boolean {
  return url === "/" ? pathname === "/" : pathname === url || pathname.startsWith(`${url}/`);
}

/** Human labels for breadcrumb segments. */
export const SEGMENT_LABELS: Record<string, string> = {
  orders: "Orders",
  customers: "Customers",
  products: "Products",
  categories: "Categories",
  farmers: "Farmers",
  reviews: "Reviews",
  media: "Media library",
  inventory: "Inventory",
  deliveries: "Deliveries",
  live: "Live orders",
  subscriptions: "Subscriptions",
  support: "Support",
  "help-faqs": "Help FAQs",
  packing: "Packing",
  riders: "Riders",
  "rider-map": "Rider map",
  coupons: "Coupons",
  banners: "Banners",
  "home-page": "Home page",
  recommendations: "Recommendations",
  announcements: "Announcements",
  notifications: "Notifications",
  refunds: "Refunds",
  wallet: "Wallet",
  invoices: "Invoices",
  settings: "Settings",
  new: "New",
  edit: "Edit",
};

/** The menu as `ability` may see it: pages it can't read are left out, and so are empty groups. */
export function visibleNavGroups(ability: AppAbility): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => ability.can("read", item.subject)),
  })).filter((group) => group.items.length > 0);
}

/** The menu entry a path belongs to (the most specific match), e.g. /products/p-1 → Products. */
export function navItemFor(pathname: string): NavItem | undefined {
  return NAV_GROUPS.flatMap((group) => group.items)
    .filter((item) => isNavActive(pathname, item.url))
    .sort((a, b) => b.url.length - a.url.length)[0];
}
